// Copyright (c) 2021 Patrick Amrein <amrein@ubique.ch>
//
// This software is released under the MIT License.
// https://opensource.org/licenses/MIT

import { UserManager } from 'oidc-client';

import jwtDecode from 'jwt-decode';
import { useState } from 'react';

const issuer = process.env.REACT_APP_ISSUER_URL
    ? process.env.REACT_APP_ISSUER_URL
    : '/issuer';
const clientId = 'emergency-locator';
const redirectUri = `${window.location.origin}/echosos/signin-oidc`;

interface Token {
    tokenString: string;
    token: unknown;
    idToken: string;
}

const urlEncode = (body: Record<string, unknown>): BodyInit =>
    Object.entries(body)
        .map(([key, value]) => `${key}=${value}`)
        .join('&');

const userManager = new UserManager({
    authority: issuer,
    client_id: clientId,
    redirect_uri: redirectUri,
    response_type: 'code',
    scope: 'openid profile',
    post_logout_redirect_uri: `${window.location.origin}`,
});

const cleanup = (): void => {
    // Remove the ?code&state from the URL
    window.history.replaceState(
        {},
        window.document.title,
        window.location.origin,
    );
};

const getTokenEndpoint = async (): Promise<string> => {
    const configResponse = await fetch(
        `${issuer}/.well-known/openid-configuration`,
    );
    const openidConfiguration = await configResponse.json();

    const tokenEndpoint = openidConfiguration.token_endpoint;
    return Promise.resolve(tokenEndpoint);
};

const tradeForToken = async (
    tokenEndpoint: string,
    code: string,
    codeVerifier: string,
): Promise<Token> => {
    const tokenResponse = await fetch(tokenEndpoint, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: urlEncode({
            client_id: clientId,
            redirect_uri: redirectUri,
            code,
            code_verifier: codeVerifier,
            grant_type: 'authorization_code',
        }),
    });
    if (!tokenResponse.ok) {
        console.error('Could not trade code for token');
        cleanup();
        return Promise.reject(new Error('Could not trade code for token'));
    }
    const tokenResponsePayload = await tokenResponse.json();
    return {
        tokenString: tokenResponsePayload.access_token,
        token: jwtDecode(tokenResponsePayload.access_token),
        idToken: tokenResponsePayload.id_token,
    };
};

interface User {
    accessToken: string;
}

export interface SigninManager {
    user?: User;
    authorities: string[];
    signin: () => Promise<void>;
    signout: () => void;
    isSignedIn: () => boolean;
}

export const useProvideAuth = (): SigninManager => {
    const tokenString = localStorage.getItem('access_token');
    const loggedInUser =
        tokenString == null ? undefined : { accessToken: tokenString };
    const [user, setUser] = useState<User | undefined>(loggedInUser);

    const urlParams = new URLSearchParams(window.location.search);
    const signin = async (): Promise<void> => {
        const code = urlParams.get('code');
        const state = urlParams.get('state');
        if (code == null || state == null) {
            // we don't have any state so just redirect to the idp
            return userManager.signinRedirect();
        }

        // fetch local code_verifier for PKCE
        const stateKey = `oidc.${state}`;
        const codeVerifier = JSON.parse(
            localStorage.getItem(stateKey) || '{}',
        ).code_verifier;
        localStorage.removeItem(stateKey);

        const tokenEndpoint = await getTokenEndpoint();
        const token = await tradeForToken(
            tokenEndpoint,
            code,
            codeVerifier,
        ).catch(() => {
            cleanup();
            return Promise.reject();
        });

        localStorage.setItem('access_token', token.tokenString);
        localStorage.setItem('id_token', token.idToken);
        setUser({ accessToken: token.tokenString });
        cleanup();
        return Promise.resolve();
    };

    const signout = (): void => {
        userManager
            .signoutRedirect({
                id_token_hint: localStorage.getItem('id_token'),
            })
            .then(() => {
                localStorage.removeItem('access_token');
                localStorage.removeItem('id_token');
            });
    };

    const isSignedIn = (): boolean => {
        return user != null;
    };

    let authorities = [];
    if (user) {
        const base64Url = user.accessToken.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(
            atob(base64)
                .split('')
                .map((c) => {
                    return `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`;
                })
                .join(''),
        );

        authorities = JSON.parse(jsonPayload).authorities.split(' ');
    }

    return {
        user,
        authorities,
        signin,
        signout,
        isSignedIn,
    };
};
